/**
 * ApprovalInbox — shows the current user's pending approval tasks.
 * Each task card shows the workflow details, form data, and lets the
 * user APPROVE, REJECT (with comment), or DELEGATE to another user.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authHeaders, errorMessage, showAdminError } from './adminApi';
import { uiLang } from './i18nUtil';

interface ApprovalTask {
  id:            string;
  instance_id:   string;
  step_number:   number;
  title:         string;
  workflow_type: string;
  description?:  string;
  form_data:     Record<string, unknown>;
  requested_by:  string;
  expires_at:    string;
  resource_type?: string;
  resource_id?:   string;
}

type Decision = 'APPROVED' | 'REJECTED' | 'ABSTAINED';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  // session token + Content-Type only with a body (see adminApi.ts); failed actions are shown
  const headers: Record<string, string> = { ...authHeaders(), ...(opts?.body != null ? { 'Content-Type': 'application/json' } : {}), ...(opts?.headers as Record<string, string> | undefined) };
  const res = await fetch(url, { ...opts, headers });
  if (!res.ok) {
    const msg = await errorMessage(res);
    if ((opts?.method ?? 'GET') !== 'GET') showAdminError(msg);
    throw new Error(msg);
  }
  return (await res.json() as { data: T }).data;
}

function TaskCard({ task, onDecided }: { task: ApprovalTask; onDecided: () => void }) {
  const { t, i18n }               = useTranslation();
  const [comment, setComment]     = useState('');
  const [delegate, setDelegate]   = useState('');
  const [mode, setMode]           = useState<'idle' | 'reject' | 'delegate'>('idle');
  const [error, setError]         = useState<string | null>(null);

  const decideMutation = useMutation({
    mutationFn: (params: { decision: Decision; comment?: string }) =>
      apiFetch(`/v1/workflows/tasks/${task.id}/decide`, {
        method: 'POST',
        body:   JSON.stringify(params),
      }),
    onSuccess: onDecided,
    onError:   (err: Error) => setError(err.message),
  });

  const delegateMutation = useMutation({
    mutationFn: (params: { delegateTo: string; reason?: string }) =>
      apiFetch(`/v1/workflows/tasks/${task.id}/delegate`, {
        method: 'POST',
        body:   JSON.stringify(params),
      }),
    onSuccess: onDecided,
    onError:   (err: Error) => setError(err.message),
  });

  const isOverdue = new Date(task.expires_at) < new Date();

  return (
    <div className="rounded-lg border border-nord-2 bg-nord-1 p-5 flex flex-col gap-3">
      {/* Title row */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="mb-1 inline-block rounded bg-nord-2 px-2 py-0.5 text-xs font-mono text-nord-muted">
            {task.workflow_type}
          </span>
          <h3 className="text-sm font-semibold text-nord-4">{task.title}</h3>
          {task.description && <p className="mt-0.5 text-xs text-nord-muted">{task.description}</p>}
        </div>
        {isOverdue && (
          <span className="rounded bg-nord-11/20 px-2 py-0.5 text-xs font-semibold text-nord-11">
            {t('adminUi.approval.overdue', 'OVERDUE')}
          </span>
        )}
      </div>

      {/* Metadata */}
      <div className="text-xs text-nord-muted space-y-0.5">
        <p>{t('adminUi.approval.requestedBy', 'Requested by')}: <span className="text-nord-4">{task.requested_by}</span></p>
        <p>{t('adminUi.approval.step', 'Step')}: {task.step_number + 1}</p>
        <p>{t('adminUi.approval.deadline', 'Due')}: {new Date(task.expires_at).toLocaleString(uiLang(i18n))}</p>
        {task.resource_type && (
          <p>{t('adminUi.approval.resource', 'Resource')}: {task.resource_type} {task.resource_id}</p>
        )}
      </div>

      {/* Form data preview */}
      {Object.keys(task.form_data).length > 0 && (
        <details className="rounded-lg bg-nord-0 p-3">
          <summary className="cursor-pointer text-xs font-medium text-nord-muted hover:text-nord-4">
            {t('adminUi.approval.formData', 'Request details')}
          </summary>
          <pre className="mt-2 overflow-auto text-xs text-nord-muted">
            {JSON.stringify(task.form_data, null, 2)}
          </pre>
        </details>
      )}

      {/* Mode: reject comment */}
      {mode === 'reject' && (
        <textarea
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('adminUi.approval.rejectReason', 'Reason for rejection…')}
          className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none resize-none"
        />
      )}

      {/* Mode: delegate */}
      {mode === 'delegate' && (
        <input
          type="text"
          value={delegate}
          onChange={(e) => setDelegate(e.target.value)}
          placeholder={t('adminUi.approval.delegateTo', 'User ID to delegate to…')}
          className="w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-4 placeholder-nord-muted focus:border-nord-frost-1 focus:outline-none"
        />
      )}

      {error && <p className="text-sm text-nord-11">{error}</p>}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => decideMutation.mutate({ decision: 'APPROVED' })}
          disabled={decideMutation.isPending}
          className="rounded-lg bg-nord-14/20 px-3 py-1.5 text-sm font-semibold text-nord-14 hover:bg-nord-14/40 disabled:opacity-50"
        >
          {t('adminUi.approval.approve', 'Approve')}
        </button>

        {mode === 'reject' ? (
          <button
            onClick={() => decideMutation.mutate({ decision: 'REJECTED', comment })}
            disabled={decideMutation.isPending || !comment}
            className="rounded-lg bg-nord-11/20 px-3 py-1.5 text-sm font-semibold text-nord-11 hover:bg-nord-11/40 disabled:opacity-50"
          >
            {t('adminUi.approval.confirmReject', 'Confirm Rejection')}
          </button>
        ) : (
          <button
            onClick={() => setMode('reject')}
            className="rounded-lg border border-nord-11 px-3 py-1.5 text-sm font-semibold text-nord-11 hover:bg-nord-11/20"
          >
            {t('adminUi.approval.reject', 'Reject')}
          </button>
        )}

        {mode === 'delegate' ? (
          <button
            onClick={() => delegateMutation.mutate({ delegateTo: delegate })}
            disabled={delegateMutation.isPending || !delegate}
            className="rounded-lg bg-nord-frost-1/20 px-3 py-1.5 text-sm font-semibold text-nord-frost-1 hover:bg-nord-frost-1/40 disabled:opacity-50"
          >
            {t('adminUi.approval.confirmDelegate', 'Confirm Delegation')}
          </button>
        ) : (
          <button
            onClick={() => setMode('delegate')}
            className="rounded-lg border border-nord-2 px-3 py-1.5 text-sm text-nord-muted hover:text-nord-4"
          >
            {t('adminUi.approval.delegate', 'Delegate')}
          </button>
        )}

        {(mode !== 'idle') && (
          <button
            onClick={() => { setMode('idle'); setComment(''); setDelegate(''); }}
            className="text-xs text-nord-muted hover:text-nord-4"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

export function ApprovalInbox() {
  const { t } = useTranslation();
  const qc    = useQueryClient();

  const tasksQ = useQuery({
    queryKey: ['approval-inbox'],
    queryFn:  () => apiFetch<{ tasks: ApprovalTask[] }>('/v1/workflows/tasks/mine'),
    refetchInterval: 30_000,
  });

  const handleDecided = () => void qc.invalidateQueries({ queryKey: ['approval-inbox'] });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-nord-4">{t('adminUi.approval.inbox', 'Approval Inbox')}</h2>
        {tasksQ.data?.tasks.length !== undefined && (
          <span className="rounded-full bg-nord-frost-1 px-2 py-0.5 text-xs font-bold text-nord-0">
            {tasksQ.data.tasks.length}
          </span>
        )}
      </div>

      {tasksQ.isLoading && <p className="text-sm text-nord-muted">{t('common.loading', 'Loading…')}</p>}
      {tasksQ.isError && <p className="text-sm text-nord-11">{(tasksQ.error as Error).message}</p>}

      {tasksQ.data?.tasks.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-nord-2 bg-nord-1 py-12 text-center">
          <p className="text-nord-4 font-medium">{t('adminUi.approval.allClear', 'All caught up!')}</p>
          <p className="text-sm text-nord-muted">{t('adminUi.approval.noTasks', 'No pending approvals.')}</p>
        </div>
      )}

      {tasksQ.data?.tasks.map((task) => (
        <TaskCard key={task.id} task={task} onDecided={handleDecided} />
      ))}
    </div>
  );
}
